import {Avatar as AvatarPrimitive} from '@base-ui/react/avatar'
import {cva, type VariantProps} from 'class-variance-authority'

import {cn} from '@utilities/helpers'

const avatarVariants = cva(
    'inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full bg-neutral-100 align-middle',
    {
        variants: {
            size: {
                sm: 'size-6 text-xs',
                md: 'size-10 text-xl',
                lg: 'size-14 text-[28px]'
            }
        },
        defaultVariants: {
            size: 'md'
        }
    }
)

const getInitials = (name: string): string => {
    const initials = name.match(/\b\w/g)
    if (initials) {
        return initials.length <= 2 ? initials.join('') : `${initials[0]}${initials.at(-1)}`
    }

    return ''
}

export type AvatarProps = Omit<AvatarPrimitive.Root.Props, 'children'> &
    VariantProps<typeof avatarVariants> & {
        name: string
        imageUrl: string
    }

function Avatar({className, size = 'md', name, imageUrl, ...props}: AvatarProps) {
    return (
        <AvatarPrimitive.Root className={cn(avatarVariants({size, className}))} data-slot="avatar" {...props}>
            <AvatarPrimitive.Image
                alt={name}
                className="aspect-square size-full rounded-full object-cover"
                data-slot="avatar-image"
                src={imageUrl}
            />
            <AvatarPrimitive.Fallback
                className="flex size-full items-center justify-center rounded-full text-neutral-700"
                data-slot="avatar-fallback"
                delay={600}
            >
                {getInitials(name)}
            </AvatarPrimitive.Fallback>
        </AvatarPrimitive.Root>
    )
}

export {Avatar, avatarVariants}
